import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import logoDark from "@/assets/logo-dark.png";
import logoLight from "@/assets/logo-light.png";
import { ThemeProvider } from "@/lib/theme";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

let captureClientExceptionRef: ((error: unknown, context?: Record<string, unknown>) => string) | null = null;

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const eventId = typeof window !== "undefined" ? (captureClientExceptionRef?.(error) ?? "") : "";
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. Try refreshing or head back home.
        </p>
        {eventId ? (
          <p className="mt-2 font-mono text-xs text-muted-foreground/70">Reference: {eventId.slice(0, 8)}</p>
        ) : null}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Super Affiliate OS, Operating system for affiliate marketers" },
      {
        name: "description",
        content:
          "Super Affiliate OS is the unified workspace for affiliate marketers: anomaly detection, contact intelligence, swipe files, and team collaboration.",
      },
      { property: "og:title", content: "Super Affiliate OS, Operating system for affiliate marketers" },
      {
        property: "og:description",
        content: "The operating system for serious affiliate marketers.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Super Affiliate OS, Operating system for affiliate marketers" },
      { name: "description", content: "Super Affiliate OS unifies affiliate program data for marketers." },
      { property: "og:description", content: "Super Affiliate OS unifies affiliate program data for marketers." },
      { name: "twitter:description", content: "Super Affiliate OS unifies affiliate program data for marketers." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/0b7680de-8f03-4afa-96fb-d4596c3772ed/id-preview-fb5fe6dd--8ef96a24-4c9b-4664-b1c8-0ecfc84c206b.lovable.app-1778826806536.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/0b7680de-8f03-4afa-96fb-d4596c3772ed/id-preview-fb5fe6dd--8ef96a24-4c9b-4664-b1c8-0ecfc84c206b.lovable.app-1778826806536.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap" },
      { rel: "icon", type: "image/png", href: logoDark, media: "(prefers-color-scheme: dark)" },
      { rel: "icon", type: "image/png", href: logoLight, media: "(prefers-color-scheme: light)" },
      { rel: "apple-touch-icon", href: logoDark },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var t=localStorage.getItem('saos-theme')||'dark';var d=t==='dark'||(t==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);if(d)document.documentElement.classList.add('dark');}catch(e){document.documentElement.classList.add('dark');}})();`,
          }}
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    void import("@/lib/sentry-browser").then(({ initSentryClient, captureClientException }) => {
      captureClientExceptionRef = captureClientException;
      void initSentryClient();
    });
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      router.invalidate();
      queryClient.invalidateQueries();
    });
    return () => subscription.unsubscribe();
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <SentryUserSync />
          <Outlet />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function SentryUserSync() {
  const { user, profile } = useAuth();
  useEffect(() => {
    void import("@/lib/sentry-browser").then(({ setSentryUserContext }) => {
      void setSentryUserContext({
        userId: user?.id ?? null,
        email: user?.email ?? null,
        isPlatformAdmin: profile?.is_platform_admin ?? false,
      });
    });
  }, [user?.id, user?.email, profile?.is_platform_admin]);
  return null;
}
